<template>
  <Section
    :id="id"
    :header="t('shipments.driverContact')"
    :loading="driverContact.loading"
    class="driver-contact-section"
    :data-class="[
      'flex flex-column flex-gap-1 relative',
      { hovering: isHovering },
    ]"
  >
    <template v-if="driverContact.data">
      <div
        class="driver-contact"
        :aria-label="
          editable
            ? t('common.editParam', { param: t('shipments.driverContact') })
            : undefined
        "
        :class="{ 'cursor-pointer': editable }"
        @click="onEdit"
        @mouseenter="isHovering = true"
        @mouseleave="isHovering = false"
      >
        <LabeledIcon
          icon="pi pi-user"
          :label="driverContact.data.name ?? '-'"
        />
        <LabeledIcon
          icon="pi pi-phone"
          :label="driverContact.data.phone ?? '-'"
        />
        <LabeledIcon
          icon="pi pi-envelope"
          :label="driverContact.data.email ?? '-'"
        />
        <LabeledIcon
          icon="pi pi-truck"
          :label="driverContact.data.trailerNumber ?? '-'"
        />
      </div>

      <FontAwesomeIcon
        v-if="editable"
        aria-hidden="true"
        class="edit-icon absolute top-0 right-0"
        :icon="['fas', 'pencil']"
      />
    </template>
    <template v-else>
      <div>
        {{ t('common.noParam', { param: t('shipments.driverContact') }) }}
      </div>

      <AddButton
        v-if="editable"
        :label="t('shipments.driverContact')"
        :disabled="driverContact.loading"
        @click="onEdit"
      />
    </template>

    <EditDriverContactModal
      v-model:visible="isShowingEditModal"
      :driver-contact="driverContact"
      @save="onSave"
    />
  </Section>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { type Loadable } from '@rygen/rygen-ui/model';

import { DriverContact } from '@/model';

import EditDriverContactModal from '@/components/shipment/EditDriverContactModal.vue';
import AddButton from '@/components/ui/AddButton.vue';
import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

export type DriverContactPayload = {
  driverContact: DriverContact;
  onSuccessCallback?: () => void;
  onErrorCallback?: (error: unknown) => void;
};

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  driverContact: {
    type: Object as PropType<Loadable<DriverContact>>,
    default: () => ({ loading: false }),
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, driverContact, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'save', payload: DriverContactPayload): void;
}>();

const { t } = useI18n();

const isShowingEditModal = ref(false);
const isHovering = ref(false);

const onEdit = () => {
  isShowingEditModal.value = editable.value;
};

const onSave = (newDriverContact: DriverContact) => {
  emit('save', {
    driverContact: newDriverContact,
    onSuccessCallback: () => (isShowingEditModal.value = false),
  });
};
</script>

<style>
.driver-contact-section {
  .section-data {
    &.hovering {
      .edit-icon {
        opacity: 1;
      }
    }

    .driver-contact {
      display: flex;
      flex-flow: column;
      gap: var(--sp-2);
    }

    .add-button {
      align-self: end;
    }

    .edit-icon {
      margin-right: var(--sp-3);
      opacity: 0;
      transition: opacity 100ms ease-in;
    }
  }
}
</style>
